import { useEffect, useRef, useState } from 'react'
import apiClient from '@/api/client'
import { useUI } from '@/stores/ui'

/**
 * Private scratch pad for the logged-in user (Teacher Dashboard "Notes", spec section 10).
 * Saved to /users/me/notes 1.5s after typing stops; nobody else can see it.
 */
export default function PersonalNotes() {
  const addToast = useUI((s) => s.addToast)
  const [content, setContent] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const saved = useRef('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    apiClient.get('/api/v1/users/me/notes')
      .then(({ data }) => { setContent(data.content || ''); saved.current = data.content || '' })
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [])

  useEffect(() => {
    if (!loaded || content === saved.current) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      setStatus('saving')
      try {
        await apiClient.put('/api/v1/users/me/notes', { content })
        saved.current = content
        setStatus('saved')
      } catch (e: any) {
        setStatus('idle')
        addToast({ message: e.response?.data?.detail || 'Could not save your notes', type: 'error' })
      }
    }, 1500)
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [content, loaded, addToast])

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <label htmlFor="personal-notes" className="text-sm font-medium text-gray-700">My notes <span className="text-gray-400 font-normal">(private — only you can see these)</span></label>
        <span className="text-xs text-gray-400" aria-live="polite">{status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved' : ''}</span>
      </div>
      <textarea
        id="personal-notes"
        className="input-field w-full min-h-[140px] focus-ring resize-y"
        placeholder="Lesson ideas, reminders, things to bring…"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        disabled={!loaded}
        title="Your private notes. They save automatically."
      />
    </div>
  )
}
